<template>
  <div class="modal" :class="{ 'is-active': isActive }">
    <div class="modal-background" @click="handleCancel"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ editMode ? 'Update Supporting Documents' : 'Add Banking Information' }}
        </p>
        <button
          class="delete"
          aria-label="close"
          @click="handleCancel"
        />
      </header>
      <section class="modal-card-body">
        <!-- Payment Info Section -->
        <div class="form-section">
          <h3 class="section-title">Banking Information</h3>
          <p v-if="editMode" class="help" style="margin-bottom: 1rem;">
            This information cannot be modified. Please upload new supporting documents below.
          </p>

          <!-- Business Name -->
          <div class="field">
            <label class="label">Business Name</label>
            <div class="control">
              <input
                v-model="formData.po_business_name"
                class="input"
                type="text"
                placeholder="Business name"
                :disabled="editMode"
              />
            </div>
          </div>

          <!-- Name on Account -->
          <div class="field">
            <label class="label">Name on Account <span v-if="!editMode">*</span></label>
            <div class="control">
              <input
                v-model="formData.name_on_account"
                class="input"
                type="text"
                placeholder="Name as it appears on account"
                :class="{ 'is-danger': errors.name_on_account }"
                :disabled="editMode"
              />
            </div>
            <p v-if="errors.name_on_account" class="help is-danger">{{ errors.name_on_account }}</p>
          </div>

          <!-- Bank Name -->
          <div class="field">
            <label class="label">Bank Name <span v-if="!editMode">*</span></label>
            <div class="control">
              <input
                v-model="formData.bank_name"
                class="input"
                type="text"
                placeholder="Bank name"
                :class="{ 'is-danger': errors.bank_name }"
                :disabled="editMode"
              />
            </div>
            <p v-if="errors.bank_name" class="help is-danger">{{ errors.bank_name }}</p>
          </div>

          <!-- Routing Number and Account Number on same line -->
          <div class="columns">
            <div class="column is-half">
              <div class="field">
                <label class="label">Routing Number <span v-if="!editMode">*</span></label>
                <div class="control">
                  <input
                    v-model="formData.routing_number"
                    class="input"
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    :placeholder="editMode ? '' : '9-digit routing number'"
                    maxlength="9"
                    :class="{ 'is-danger': errors.routing_number }"
                    :disabled="editMode"
                  />
                </div>
                <p v-if="errors.routing_number" class="help is-danger">{{ errors.routing_number }}</p>
              </div>
            </div>

            <div class="column is-half">
              <div class="field">
                <label class="label">Account Number <span v-if="!editMode">*</span></label>
                <div class="control">
                  <input
                    v-model="formData.account_number"
                    class="input"
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    :placeholder="editMode ? '' : 'Account number'"
                    maxlength="17"
                    :class="{ 'is-danger': errors.account_number }"
                    :disabled="editMode"
                  />
                </div>
                <p v-if="errors.account_number" class="help is-danger">{{ errors.account_number }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tax ID Section -->
        <div class="form-section">
          <h3 class="section-title">Tax Identification</h3>

          <!-- Tax ID Type Selection -->
          <div class="field">
            <label class="label">Tax ID Type <span v-if="!editMode">*</span></label>
            <div class="control">
              <label class="radio">
                <input
                  type="radio"
                  v-model="formData.tax_id_type"
                  value="SSN"
                  :disabled="editMode"
                />
                SSN (Social Security Number)
              </label>
              <label class="radio" style="margin-left: 1.5rem;">
                <input
                  type="radio"
                  v-model="formData.tax_id_type"
                  value="EIN"
                  :disabled="editMode"
                />
                EIN (Employer Identification Number)
              </label>
            </div>
            <p v-if="errors.tax_id_type" class="help is-danger">{{ errors.tax_id_type }}</p>
          </div>

          <!-- SSN Number (shown when SSN selected) -->
          <div v-if="formData.tax_id_type === 'SSN'" class="field">
            <label class="label">SSN Number <span v-if="!editMode">*</span></label>
            <div class="control">
              <input
                v-model="formData.ssn_number"
                class="input"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                :placeholder="editMode ? '' : '9-digit SSN'"
                maxlength="9"
                :class="{ 'is-danger': errors.ssn_number }"
                :disabled="editMode"
              />
            </div>
            <p v-if="errors.ssn_number" class="help is-danger">{{ errors.ssn_number }}</p>
          </div>

          <!-- EIN Number (shown when EIN selected) -->
          <div v-if="formData.tax_id_type === 'EIN'" class="field">
            <label class="label">EIN Number <span v-if="!editMode">*</span></label>
            <div class="control">
              <input
                v-model="formData.ein_number"
                class="input"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                :placeholder="editMode ? '' : '9-digit EIN'"
                maxlength="9"
                :class="{ 'is-danger': errors.ein_number }"
                :disabled="editMode"
              />
            </div>
            <p v-if="errors.ein_number" class="help is-danger">{{ errors.ein_number }}</p>
          </div>
        </div>

        <!-- Billing Address Section -->
        <div class="form-section">
          <h3 class="section-title">W9 Address</h3>

          <!-- Street Address -->
          <div class="field">
            <label class="label">Street Address <span v-if="!editMode">*</span></label>
            <div class="control">
              <input
                v-model="formData.w9_address_street_1"
                class="input"
                type="text"
                :placeholder="editMode ? '' : '123 Main Street'"
                :class="{ 'is-danger': errors.w9_address_street_1 }"
                :disabled="editMode"
              />
            </div>
            <p v-if="errors.w9_address_street_1" class="help is-danger">{{ errors.w9_address_street_1 }}</p>
          </div>

          <!-- Apartment/Suite/Unit -->
          <div class="field">
            <label class="label">Apartment, Suite, Unit (Optional)</label>
            <div class="control">
              <input
                v-model="formData.w9_address_street_2"
                class="input"
                type="text"
                :placeholder="editMode ? '' : 'Apt 2B'"
                :disabled="editMode"
              />
            </div>
          </div>

          <!-- City and ZIP Row -->
          <div class="columns">
            <div class="column is-half">
              <div class="field">
                <label class="label">City <span v-if="!editMode">*</span></label>
                <div class="control">
                  <input
                    v-model="formData.w9_address_city"
                    class="input"
                    type="text"
                    :placeholder="editMode ? '' : 'Philadelphia'"
                    :class="{ 'is-danger': errors.w9_address_city }"
                    :disabled="editMode"
                  />
                </div>
                <p v-if="errors.w9_address_city" class="help is-danger">{{ errors.w9_address_city }}</p>
              </div>
            </div>

            <div class="column is-half">
              <div class="field">
                <label class="label">ZIP Code <span v-if="!editMode">*</span></label>
                <div class="control">
                  <input
                    v-model="formData.w9_address_postal_code"
                    class="input"
                    type="text"
                    :placeholder="editMode ? '' : '19103'"
                    maxlength="5"
                    :class="{ 'is-danger': errors.w9_address_postal_code }"
                    :disabled="editMode"
                  />
                </div>
                <p v-if="errors.w9_address_postal_code" class="help is-danger">{{ errors.w9_address_postal_code }}</p>
              </div>
            </div>
          </div>

          <!-- State on its own line -->
          <div class="field">
            <label class="label">State <span v-if="!editMode">*</span></label>
            <template v-if="editMode">
              <div class="control">
                <input
                  v-model="formData.w9_address_state"
                  class="input"
                  type="text"
                  disabled
                />
              </div>
            </template>
            <template v-else>
              <dropdown innerLabel="false"
                v-model="formData.w9_address_state"
                :options="stateOptions"
                placeholder="Select state"
                textKey="text"
                valueKey="value"
                :error="errors.w9_address_state"
              />
            </template>
            <p v-if="errors.w9_address_state" class="help is-danger">{{ errors.w9_address_state }}</p>
          </div>
        </div>

        <!-- Document Upload Section -->
        <div class="form-section">
          <h3 class="section-title">Supporting Documents <span class="has-text-love-park-red">*</span></h3>
          <p class="help" style="margin-bottom: 1rem;">
            Please upload both a Bank Account Document (e.g., voided check or bank statement) and a W9.
            Accepted formats: PDF, PNG, JPEG. Maximum file size: 4.5MB.
          </p>

          <!-- File Upload -->
          <div class="field">
            <div class="file has-name" :class="{ 'is-primary': uploadedFiles.length > 0 }">
              <label class="file-label">
                <input
                  class="file-input"
                  type="file"
                  ref="fileInput"
                  @change="handleFileSelect"
                  accept="image/png,image/jpeg,application/pdf"
                  multiple
                />
                <span class="file-cta">
                  <span class="file-icon">
                    <i class="fas fa-upload"></i>
                  </span>
                  <span class="file-label">Choose file(s)...</span>
                </span>
                <span v-if="uploadedFiles.length > 0" class="file-name" style="border: none; background: transparent;">
                  {{ uploadedFiles.length }} file(s) selected
                </span>
              </label>
            </div>
            <p v-if="errors.files" class="help is-danger">{{ errors.files }}</p>
          </div>

          <!-- File List with Document Type Selection -->
          <div v-if="uploadedFiles.length > 0" class="file-grid" style="margin-top: 1rem;">
            <div class="columns is-multiline">
              <div
                v-for="(file, index) in uploadedFiles"
                :key="file.id"
                class="column is-half"
              >
                <div class="box" style="padding: 0.75rem; margin-bottom: 0;">
                  <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
                    <div style="display: flex; align-items: center; overflow: hidden; flex: 1;">
                      <i class="fas fa-file" style="margin-right: 0.5rem; color: #2176d2; flex-shrink: 0;"></i>
                      <span class="is-size-7" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ file.name }}</span>
                    </div>
                    <button
                      class="delete is-small"
                      type="button"
                      @click="removeFile(index)"
                      aria-label="Remove file"
                      style="flex-shrink: 0; margin-left: 0.5rem;"
                    ></button>
                  </div>
                  <div class="field" style="margin-bottom: 0;">
                    <div class="control">
                      <div class="select is-small is-fullwidth">
                        <select v-model="fileDocumentTypes[index]" :class="{ 'is-danger': errors[`fileType_${index}`] }">
                          <option value="">Select type...</option>
                          <option
                            v-for="docType in documentTypeOptions"
                            :key="docType.value"
                            :value="docType.value"
                            :disabled="isDocTypeDisabled(docType.value, index)"
                          >
                            {{ docType.label }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <p v-if="errors[`fileType_${index}`]" class="help is-danger is-size-7" style="margin-top: 0.25rem;">
                    {{ errors[`fileType_${index}`] }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>
      <footer class="modal-card-foot" style="display: flex; flex-direction: column; align-items: flex-start;">
        <!-- Loading Message -->
        <div v-if="isSubmitting && loadingMessage" class="notification has-background-ben-franklin-blue-light" style="margin-bottom: 1rem; width: 100%;">
          <p class="has-text-dark-ben-franklin">
            <span class="icon">
              <i class="fas fa-spinner fa-pulse"></i>
            </span>
            {{ loadingMessage }}
          </p>
        </div>

        <!-- Error Message in Footer -->
        <div v-if="errors.general" class="notification has-background-love-park-red-light" style="margin-bottom: 1rem; width: 100%;">
          <p class="has-text-love-park-red">{{ errors.general }}</p>
        </div>

        <div class="buttons">
          <button class="button is-primary" @click="handleSubmit" :class="{ 'is-loading': isSubmitting }">
            <span class="icon">
              <i class="fas fa-check"></i>
            </span>
            <span>{{ editMode ? 'Submit Documents' : 'Add Banking Information' }}</span>
          </button>
          <button class="button is-secondary" @click="handleCancel" :disabled="isSubmitting">
            Cancel
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import options from "@/utilities/options.js";
import { getBase64 } from "@/utilities/functions.js";

export default {
  name: "AddPaymentModal",
  mixins: [options],
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    paymentData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        po_business_name: "",
        name_on_account: "",
        bank_name: "",
        routing_number: "",
        account_number: "",
        w9_address_street_1: "",
        w9_address_street_2: "",
        w9_address_city: "",
        w9_address_state: "Pennsylvania",
        w9_address_postal_code: "",
        tax_id_type: "",
        ssn_number: "",
        ein_number: "",
      },
      uploadedFiles: [],
      fileDocumentTypes: [],
      documentTypeOptions: [
        { value: "W9", label: "W9" },
        { value: "Bank Account Document", label: "Bank Account Document" }
      ],
      errors: {},
      isSubmitting: false,
      loadingMessage: '',
      nextFileId: 0,
    };
  },
  computed: {
    stateOptions() {
      // Transform States array from options mixin into dropdown format
      return this.States.map(state => ({
        value: state,
        text: state,
      }));
    },
    usedDocumentTypes() {
      return this.fileDocumentTypes.filter(type => type !== "");
    },
  },
  watch: {
    paymentData: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.editMode) {
          this.populateFormForEdit(newVal);
        }
      },
    },
    isActive(newVal) {
      if (!newVal) {
        this.resetForm();
      } else if (this.editMode && this.paymentData) {
        this.populateFormForEdit(this.paymentData);
      }
    },
  },
  methods: {
    populateFormForEdit(payment) {
      // Populate form with existing payment data (masked for display)
      this.formData.po_business_name = payment.po_business_name || '';
      this.formData.name_on_account = payment.name_on_account || '';
      this.formData.bank_name = payment.bank_name || '';
      // Mask routing and account numbers
      this.formData.routing_number = payment.routing_number ? '****' + payment.routing_number.slice(-4) : '';
      this.formData.account_number = '****' + (payment.last_four_account_number || '');
      this.formData.w9_address_street_1 = payment.w9_address_street_1 || '';
      this.formData.w9_address_street_2 = payment.w9_address_street_2 || '';
      this.formData.w9_address_city = payment.w9_address_city || '';
      this.formData.w9_address_state = payment.w9_address_state || '';
      this.formData.w9_address_postal_code = payment.w9_address_postal_code || '';
      this.formData.tax_id_type = payment.tax_id_type || '';
      // Mask tax IDs
      if (payment.tax_id_type === 'SSN' && payment.ssn_number) {
        this.formData.ssn_number = '***-**-' + payment.ssn_number.slice(-4);
      } else {
        this.formData.ssn_number = '';
      }
      if (payment.tax_id_type === 'EIN' && payment.ein_number) {
        this.formData.ein_number = '**-***' + payment.ein_number.slice(-4);
      } else {
        this.formData.ein_number = '';
      }
    },
    isDocTypeDisabled(docType, currentFileIndex) {
      const currentSelection = this.fileDocumentTypes[currentFileIndex];
      // Don't disable if this is the current file's own selection
      if (currentSelection === docType) return false;
      // Disable if another file is using this type
      return this.usedDocumentTypes.includes(docType);
    },
    async handleFileSelect(event) {
      const files = Array.from(event.target.files);

      for (const file of files) {
        // Validate file type
        const validTypes = ['image/png', 'image/jpeg', 'application/pdf'];
        const validExtensions = ['png', 'jpg', 'jpeg', 'pdf'];
        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
          this.errors.files = `Invalid file type: ${file.name}. Only PDF, PNG, and JPEG files are accepted.`;
          continue;
        }

        // Validate file size (4.5MB)
        const maxSize = 4.5 * 1024 * 1024;
        if (file.size > maxSize) {
          this.errors.files = `File too large: ${file.name}. Maximum size is 4.5MB.`;
          continue;
        }

        // Add file to uploaded files list
        this.uploadedFiles.push({
          id: this.nextFileId++,
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
        });

        // Initialize document type for this file
        this.fileDocumentTypes.push("");
      }

      // Clear input so same file can be selected again
      event.target.value = '';

      // Clear error if files added successfully
      if (this.uploadedFiles.length > 0) {
        this.errors.files = null;
      }
    },
    validateForm() {
      this.errors = {};
      let isValid = true;

      // Only validate form fields in add mode (not edit mode)
      if (!this.editMode) {
        // Name on Account validation
        if (!this.formData.name_on_account || this.formData.name_on_account.trim() === "") {
          this.errors.name_on_account = "Name on account is required";
          isValid = false;
        }

        // Bank Name validation
        if (!this.formData.bank_name || this.formData.bank_name.trim() === "") {
          this.errors.bank_name = "Bank name is required";
          isValid = false;
        }

        // Routing Number validation (must be exactly 9 digits)
        if (!this.formData.routing_number) {
          this.errors.routing_number = "Routing number is required";
          isValid = false;
        } else if (!/^\d{9}$/.test(this.formData.routing_number)) {
          this.errors.routing_number = "Routing number must be exactly 9 digits";
          isValid = false;
        }

        // Account Number validation (typically 4-17 digits)
        if (!this.formData.account_number) {
          this.errors.account_number = "Account number is required";
          isValid = false;
        } else if (!/^\d{4,17}$/.test(this.formData.account_number)) {
          this.errors.account_number = "Account number must be between 4 and 17 digits";
          isValid = false;
        }

        // Tax ID Type validation
        if (!this.formData.tax_id_type) {
          this.errors.tax_id_type = "Please select SSN or EIN";
          isValid = false;
        } else if (this.formData.tax_id_type === "SSN") {
          if (!this.formData.ssn_number) {
            this.errors.ssn_number = "SSN is required";
            isValid = false;
          } else if (!/^\d{9}$/.test(this.formData.ssn_number)) {
            this.errors.ssn_number = "SSN must be exactly 9 digits";
            isValid = false;
          }
        } else if (this.formData.tax_id_type === "EIN") {
          if (!this.formData.ein_number) {
            this.errors.ein_number = "EIN is required";
            isValid = false;
          } else if (!/^\d{9}$/.test(this.formData.ein_number)) {
            this.errors.ein_number = "EIN must be exactly 9 digits";
            isValid = false;
          }
        }

        // Street Address validation
        if (!this.formData.w9_address_street_1 || this.formData.w9_address_street_1.trim() === "") {
          this.errors.w9_address_street_1 = "Street address is required";
          isValid = false;
        }

        // City validation
        if (!this.formData.w9_address_city || this.formData.w9_address_city.trim() === "") {
          this.errors.w9_address_city = "City is required";
          isValid = false;
        }

        // State validation
        if (!this.formData.w9_address_state) {
          this.errors.w9_address_state = "State is required";
          isValid = false;
        }

        // ZIP Code validation
        if (!this.formData.w9_address_postal_code || this.formData.w9_address_postal_code.trim() === "") {
          this.errors.w9_address_postal_code = "ZIP code is required";
          isValid = false;
        } else if (!/^\d{5}$/.test(this.formData.w9_address_postal_code)) {
          this.errors.w9_address_postal_code = "ZIP code must be 5 digits";
          isValid = false;
        }
      }

      // Validate document requirements (both modes)
      const hasBankDoc = this.fileDocumentTypes.includes("Bank Account Document");
      const hasW9 = this.fileDocumentTypes.includes("W9");

      if (!hasBankDoc && !hasW9) {
        this.errors.files = "Both a Bank Account Document and a W9 are required";
        isValid = false;
      } else if (!hasBankDoc) {
        this.errors.files = "A Bank Account Document is required";
        isValid = false;
      } else if (!hasW9) {
        this.errors.files = "A W9 is required";
        isValid = false;
      }

      // Validate document types for uploaded files
      for (let i = 0; i < this.uploadedFiles.length; i++) {
        if (!this.fileDocumentTypes[i]) {
          this.errors[`fileType_${i}`] = "Please select a document type";
          isValid = false;
        }
      }

      return isValid;
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;
      this.loadingMessage = this.editMode ? 'Uploading documents...' : 'Creating banking information...';

      try {
        const userInfo = this.$store.getters['auth/userInfo'];
        const organization_rid = userInfo?.qbUser?.related_organization;

        if (!organization_rid) {
          this.errors.general = 'Unable to determine your organization. Please try again.';
          this.isSubmitting = false;
          return;
        }

        let bankInfoRid;

        if (this.editMode) {
          // Edit mode: use existing payment rid
          bankInfoRid = this.paymentData?.rid;
          if (!bankInfoRid) {
            this.errors.general = 'Unable to determine the payment record. Please try again.';
            this.isSubmitting = false;
            return;
          }
        } else {
          // Add mode: create payment method first
          const paymentData = {
            po_business_name: this.formData.po_business_name,
            name_on_account: this.formData.name_on_account,
            bank_name: this.formData.bank_name,
            routing_number: this.formData.routing_number,
            account_number: this.formData.account_number,
            w9_address_street_1: this.formData.w9_address_street_1,
            w9_address_street_2: this.formData.w9_address_street_2,
            w9_address_city: this.formData.w9_address_city,
            w9_address_state: this.formData.w9_address_state,
            w9_address_postal_code: this.formData.w9_address_postal_code,
            ssn_number: this.formData.tax_id_type === "SSN" ? this.formData.ssn_number : "",
            ein_number: this.formData.tax_id_type === "EIN" ? this.formData.ein_number : "",
          };

          console.log("SUBMITTING PAYMENT INFO", paymentData);

          const paymentResult = await this.$store.dispatch('dashboard/submitPayment', {
            organization_rid,
            paymentData
          });

          if (!paymentResult.success) {
            this.errors.general = paymentResult.message;
            this.isSubmitting = false;
            return;
          }

          bankInfoRid = paymentResult.payment.rid;
          console.log("Payment created with RID:", bankInfoRid);
        }

        // Upload files in parallel
        if (this.uploadedFiles.length > 0) {
          this.loadingMessage = 'Uploading files...';

          // Convert files to base64 and upload in parallel
          const fileUploads = await Promise.all(
            this.uploadedFiles.map(async (file, index) => {
              const base64Data = await getBase64(file.file);
              return this.$store.dispatch('dashboard/uploadFileForPayment', {
                bankInfoRid,
                file: {
                  name: file.name,
                  base64Data
                },
                documentType: this.fileDocumentTypes[index]
              });
            })
          );

          // Check if any file uploads failed
          const failedUploads = fileUploads.filter(result => !result.success);
          if (failedUploads.length > 0) {
            console.warn(`${failedUploads.length} file(s) failed to upload:`, failedUploads);
            if (this.editMode) {
              this.errors.general = `${failedUploads.length} file(s) failed to upload. Please try again.`;
              this.isSubmitting = false;
              return;
            } else {
              this.errors.general = `Payment method created, but ${failedUploads.length} file(s) failed to upload. Please contact a developer to investigate this issue.`;
              // Still emit success since payment was created
              this.$emit("submit", this.paymentData || {});
              this.resetForm();
              this.isSubmitting = false;
              return;
            }
          }

          console.log("All files uploaded successfully");
        }

        // Refetch payments to get updated status from QuickBase
        if (this.editMode) {
          this.loadingMessage = 'Refreshing data...';
          await this.$store.dispatch('dashboard/fetchPayments', organization_rid);
        }

        // Success!
        this.$emit("submit", this.paymentData || {});
        this.resetForm();

      } catch (error) {
        console.error('Unexpected error adding payment method:', error);
        this.errors.general = 'An unexpected error occurred. Please try again.';
      } finally {
        this.isSubmitting = false;
        this.loadingMessage = '';
      }
    },
    handleCancel() {
      this.$emit("close");
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        po_business_name: "",
        name_on_account: "",
        bank_name: "",
        routing_number: "",
        account_number: "",
        w9_address_street_1: "",
        w9_address_street_2: "",
        w9_address_city: "",
        w9_address_state: "Pennsylvania",
        w9_address_postal_code: "",
        tax_id_type: "",
        ssn_number: "",
        ein_number: "",
      };
      this.uploadedFiles = [];
      this.fileDocumentTypes = [];
      this.errors = {};
      this.nextFileId = 0;

      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
      this.fileDocumentTypes.splice(index, 1);
      // Clear any error for the removed file
      delete this.errors[`fileType_${index}`];
    },
  },
};
</script>

<style scoped>
.medium-gray {
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
}

/* Adjust modal z-index to be above dashboard header (z-index: 1001) */
.modal {
  z-index: 1003;
}

.modal-background {
  z-index: 1003;
}

.modal-card {
  max-width: 1200px;
  margin-top: 80px; /* Position below header */
  z-index: 1004;
  position: relative;
}

.modal-close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
}

.modal-card-body {
  max-height: calc(100vh - 180px); /* Account for header + modal footer */
  overflow-y: auto;
  padding: 2rem 2.5rem;
}

.modal-card-foot {
  justify-content: flex-start;
}

/* Form Sections */
.form-section {
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #f0f0f0; /* Ghost Gray - Philly Style Guide */
}

.form-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  color: #000000;
  margin-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0; /* Ghost Gray - Philly Style Guide */
}

/* Form field spacing */
.field {
  margin-bottom: 1rem;
}

.columns {
  margin-bottom: 0;
}

.columns .column .field {
  margin-bottom: 0;
}

/* Ensure consistent label height across columns */
.label {
  color: #444444; /* Dark Gray - Philly Style Guide */
  font-weight: 700;
  min-height: 1.5rem;
  display: block;
}

.help {
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
}

.help.is-danger {
  color: #cc3000; /* Love Park Red - Philly Style Guide */
}

.input.is-danger {
  border-color: #cc3000; /* Love Park Red - Philly Style Guide */
}

.input:focus {
  border-color: #2176d2; /* Ben Franklin Blue - Philly Style Guide */
  box-shadow: 0 0 0 0.125em rgba(33, 118, 210, 0.25);
}

input::placeholder {
  color: #999999 !important;
  opacity: 1 !important;
}

/* Disabled input styling */
.input[disabled],
.input:disabled {
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
}
</style>
